import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import Checkbox from '@components/common/checkbox/main';
import Tooltip from '@components/common/tooltip/main';
import { get as getLabels } from '@api/v4/resources/labels';

function Summary({
  ruleType,
  scope,
  actionableResources,
  actionableKeys,
  enforce,
  updateEnforce
}) {
  const [scopeResources, updateScopeResources] = useState([]);

  const scopeType = ruleType.split("::")[1];

  const actionableType = () => {
    if (ruleType.includes("CollectionAssociation")) {
      return "Collection";
    }
    if (ruleType.includes("LabelAssociation")) {
      return "Label";
    }
    if (ruleType.includes("SectionAssociation")) {
      return "Section";
    }
    return undefined;
  };

  useEffect(() => {
    if (scopeType === "Label") {
      getLabels().then((resources) => {
        updateScopeResources(resources);
      });
    }
  }, [scopeType]);


  const scopeText = () => {
    switch (scopeType) {
      case "Tag": {
        const shouldPluralize = scope.tag_names.length > 1;
        return (
          <React.Fragment>
            <div className="trigger-intro">
              {t(`If an asset is tagged with
                ${shouldPluralize ? "any of the following" : ""} tag${shouldPluralize ? "s" : ""}:`)}
            </div>
            <div className="trigger-name-list">
              { scope.tag_names.map((name) => <div key={name}>{name}</div>) }
            </div>
          </React.Fragment>
        );
      }
      case "Label": {
        const labelNames = scopeResources.filter(
          (label) => scope.label_keys.includes(label.id)
        ).map((label) => label.attributes.name);
        const shouldPluralize = labelNames.length > 1;
        return (
          <React.Fragment>
            <div className="trigger-intro">
              {t(`If an asset is added to
                ${shouldPluralize ? "any of the following" : ""}
                label${shouldPluralize ? "s" : ""}:`)}
            </div>
            <div className="trigger-name-list">
              { labelNames.map((name) => <div key={name}>{name}</div>) }
            </div>
          </React.Fragment>
        );
      }
      default:
        return undefined;
    }
  };


  const actionableNames = () => (
    actionableResources.map((resource) => {
      if (actionableKeys.includes(resource.id)) {
        return resource.attributes.name;
      }
      return null;
    }).filter((name) => name !== null)
  );

  const actionableList = () => (
    <div className="resource-list-container">
      <div className="actionable-intro">
        {`${t("Then add it to")}
        ${actionableKeys.length > 1 ? actionableKeys.length : ''}
        ${actionableKeys.length > 1 ? `${t(actionableType().toLowerCase())}s`
          : `${t(actionableType().toLowerCase())}`}: `}
      </div>
      <div className="actionable-name-list">
        { actionableNames().map((name) => <div key={name}>{name}</div>) }
      </div>
    </div>
  );

  const enforceView = () => {
    if (ruleType === "Rule::Label::CollectionAssociation") {
      return (
        <div className="enforce-container">
          <Checkbox
            size="sm"
            checked={enforce}
            labelHtml={<div className="checkbox-text"> {t('Enforce Reverse Action')}</div>}
            toggle={() => updateEnforce(!enforce)}
          />
          <Tooltip
            tooltipContent={t('If rule no longer applies to asset, it will be removed from selected resource(s).')}
            tooltipId="select-section-info"
          >
            <span className="bff-tool-tip icon" aria-label="select section" />
          </Tooltip>
        </div>
      );
    }
    return undefined;
  };

  return (
    <div className="summary">
      <div>{scopeText()}</div>
      <div>{actionableList()}</div>
      <div>{enforceView()}</div>
    </div>
  );
}

Summary.propTypes = {
  ruleType: PropTypes.string.isRequired,
  actionableKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  actionableResources: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string
  })).isRequired,
  scope: PropTypes.shape({
    label_keys: PropTypes.arrayOf(PropTypes.string),
    tag_names: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  enforce: PropTypes.bool.isRequired,
  updateEnforce: PropTypes.func.isRequired
};

export default Summary;
